import i18next from "i18next";
import {
  FIELD_KEYWORDS_AND_KEYWORDS_NEGATIVE_REPEATED,
  FIELD_KEYWORDS_REPEATED,
  FIELD_REQUIRED,
  FIELD_MAX_LENGTH,
  FIELD_TEXT_REPEATED,
  FIELD_URL_VALID_WITH_PROTOCOL,
  FIELD_WEBPAGE_CRITERIA_REPEATED,
  FIELD_WEBPAGE_CRITERIA_REQUIRED,
  FIELD_CONSECUTIVE_UPPERCASE,
  FIELD_EMOJI_NOT_ALLOWED,
  FIELD_MIN_TEXTS_VALID,
  FIELD_MAX_TEXTS_VALID,
  FIELD_MAX_IMAGES_VALID,
  FIELD_MAX_VIDEOS_YOUTUBE_VALID,
  FIELD_VIDEO_YOUTUBE_URL_INVALID,
} from "../../../../../i18n/keysTranslations";
import { z } from "zod";
import {
  GOOGLE,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
  ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION,
  ADS_GOOGLE_WEBPAGE_CRITERION_VALUE_MAX_LENGTH,
  getGoogleSearchAdGroupType,
} from "../../../../../utils/constants";
import {
  getArrayIntersection,
  getDuplicateItemsArray,
} from "../../../../../utils/array";
import {
  REGEX_URL_WITH_PROTOCOL_REQUIRED,
  REGEX_YOUTUBE_URL,
  THREE_UPPERCASE_REGEX,
} from "../../../../../utils/regex";
import emojiRegex from "emoji-regex";

// Create emoji regex instance
const emojiRegexInstance = emojiRegex();

// Function to validate that text doesn't contain emojis
function validateNoEmojis(text) {
  if (!text || typeof text !== "string") return true;
  return !emojiRegexInstance.test(text);
}

function getIndicesMap(arrayOfStrings) {
  const map = {};
  arrayOfStrings.forEach((txt, i) => {
    if (!map[txt]) {
      map[txt] = [];
    }
    map[txt].push(i);
  });
  return map;
}

export const adGroupGoogleBaseSchema = z.object({
  ads: z.array(z.any()).optional(),
  platform: z.literal(GOOGLE),
  keywords: z.array(z.string()).optional(),
  keywordsNegative: z.array(z.string()).optional(),
  type: z.any().optional(),
  webpages: z.any().optional(),
  // App campaign fields
  destinationUrl: z.any().optional(),
  titles: z.any().optional(),
  descriptions: z.any().optional(),
  imageUrls: z.any().optional(),
  videoUrls: z.any().optional(),
});

export const validateAdGroupGoogle = ({
  campaign,
  adGroup,
  indexAdGroup,
  ctx,
}) => {
  const basicPaths = ["adsGroups", indexAdGroup];
  const platformData = campaign?.platformsData?.[GOOGLE];
  const objective = platformData?.objective;

  // Validations for SEARCH campaigns
  if (objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION) {
    const type = getGoogleSearchAdGroupType(adGroup);
    const { keywords, keywordsNegative = [], webpages = [] } = adGroup;

    if (type === ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION) {
      if (!webpages || webpages.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "webpages"],
          message: i18next.t(FIELD_WEBPAGE_CRITERIA_REQUIRED),
        });
      }

      const criterionKeys = [];
      webpages.forEach((row, idx) => {
        const val = row?.value?.trim() || "";

        if (!val) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "webpages", idx, "value"],
            message: i18next.t(FIELD_REQUIRED),
          });
        } else if (val.length > ADS_GOOGLE_WEBPAGE_CRITERION_VALUE_MAX_LENGTH) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "webpages", idx, "value"],
            message: i18next.t(FIELD_MAX_LENGTH, {
              value: ADS_GOOGLE_WEBPAGE_CRITERION_VALUE_MAX_LENGTH,
            }),
          });
        } else if (!REGEX_URL_WITH_PROTOCOL_REQUIRED.test(encodeURI(val))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "webpages", idx, "value"],
            message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
          });
        }

        if (val) {
          criterionKeys.push(val.toLowerCase());
        }
      });

      const dupCriteria = getDuplicateItemsArray(criterionKeys);
      if (dupCriteria.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "webpages"],
          message: i18next.t(FIELD_WEBPAGE_CRITERIA_REPEATED, {
            value: dupCriteria.join(", "),
          }),
        });
      }

      if (keywordsNegative?.length > 0) {
        const duplicatesKeywordsNegative =
          getDuplicateItemsArray(keywordsNegative);
        if (duplicatesKeywordsNegative.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "keywordsNegative"],
            message: i18next.t(FIELD_KEYWORDS_REPEATED, {
              value: duplicatesKeywordsNegative.join(", "),
            }),
          });
        }
      }
    } else {
      if (!keywords || keywords.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "keywords"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      const duplicatesKeywords = getDuplicateItemsArray(keywords);
      if (duplicatesKeywords.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "keywords"],
          message: i18next.t(FIELD_KEYWORDS_REPEATED, {
            value: duplicatesKeywords.join(", "),
          }),
        });
      }

      if (keywordsNegative?.length > 0) {
        const duplicatesKeywordsNegative =
          getDuplicateItemsArray(keywordsNegative);
        if (duplicatesKeywordsNegative.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "keywordsNegative"],
            message: i18next.t(FIELD_KEYWORDS_REPEATED, {
              value: duplicatesKeywordsNegative.join(", "),
            }),
          });
        }
      }

      if (keywordsNegative?.length > 0 && keywords?.length > 0) {
        const intersection = getArrayIntersection(keywords, keywordsNegative);
        if (intersection.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "keywordsNegative"],
            message: i18next.t(FIELD_KEYWORDS_AND_KEYWORDS_NEGATIVE_REPEATED, {
              value: intersection.join(", "),
            }),
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "keywords"],
            message: i18next.t(FIELD_KEYWORDS_AND_KEYWORDS_NEGATIVE_REPEATED, {
              value: intersection.join(", "),
            }),
          });
        }
      }
    }
  }

  // Validations for APP campaigns
  if (objective === ADS_CAMPAIGN_TYPE_APP_OPTION) {
    const minTitles = 2;
    const maxTitles = 5;
    const minDescriptions = 1;
    const maxDescriptions = 5;
    const maxImages = 20;
    const maxVideos = 20;

    // destinationUrl validation
    if (adGroup.destinationUrl) {
      if (
        !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(
          encodeURI(adGroup.destinationUrl)
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "destinationUrl"],
          message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
        });
      }
    }

    // imageUrls validation
    if (adGroup.imageUrls) {
      if (adGroup.imageUrls.length > maxImages) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "imageUrls"],
          message: i18next.t(FIELD_MAX_IMAGES_VALID, { value: maxImages }),
        });
      }
    }

    // videoUrls validation
    if (adGroup.videoUrls) {
      if (adGroup.videoUrls.length > maxVideos) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "videoUrls"],
          message: i18next.t(FIELD_MAX_VIDEOS_YOUTUBE_VALID, {
            value: maxVideos,
          }),
        });
      }

      adGroup.videoUrls.forEach((item, idx) => {
        if (!item) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "videoUrls", idx],
            message: i18next.t(FIELD_REQUIRED),
          });
        } else if (!REGEX_YOUTUBE_URL.test(item)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "videoUrls", idx],
            message: i18next.t(FIELD_VIDEO_YOUTUBE_URL_INVALID),
          });
        }
      });

      const videoUrlsNormalized = adGroup.videoUrls
        .map((url) => url?.trim().toLowerCase())
        .filter(Boolean);
      const videoUrlsMap = getIndicesMap(videoUrlsNormalized);

      Object.entries(videoUrlsMap).forEach(([, indexes]) => {
        if (indexes.length > 1) {
          indexes.forEach((i) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "videoUrls", i],
              message: i18next.t(FIELD_TEXT_REPEATED),
            });
          });
        }
      });
    }

    // titles validation
    if (!adGroup.titles || adGroup.titles.length < minTitles) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "titles"],
        message: i18next.t(FIELD_MIN_TEXTS_VALID, { value: minTitles }),
      });
    } else if (adGroup.titles.length > maxTitles) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "titles"],
        message: i18next.t(FIELD_MAX_TEXTS_VALID, { value: maxTitles }),
      });
    }
    adGroup.titles?.forEach((item, idx) => {
      if (!item.value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "titles", idx, "value"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
      if (
        item.value &&
        item.value.length > ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[GOOGLE]
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "titles", idx, "value"],
          message: i18next.t(FIELD_MAX_LENGTH, {
            value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[GOOGLE],
          }),
        });
      }
      if (item.value && THREE_UPPERCASE_REGEX.test(item.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "titles", idx, "value"],
          message: i18next.t(FIELD_CONSECUTIVE_UPPERCASE, { value: 3 }),
        });
      }
      if (item.value && !validateNoEmojis(item.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "titles", idx, "value"],
          message: i18next.t(FIELD_EMOJI_NOT_ALLOWED),
        });
      }
    });

    // Validation for repeated texts in titles
    if (adGroup.titles && adGroup.titles.length > 0) {
      const titlesNormalized = adGroup.titles
        .map((item) => item.value?.trim().toLowerCase())
        .filter(Boolean);
      const titlesMap = getIndicesMap(titlesNormalized);

      Object.entries(titlesMap).forEach(([, indexes]) => {
        if (indexes.length > 1) {
          indexes.forEach((i) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "titles", i, "value"],
              message: i18next.t(FIELD_TEXT_REPEATED),
            });
          });
        }
      });
    }

    // descriptions validation
    if (
      !adGroup.descriptions ||
      adGroup.descriptions.length < minDescriptions
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "descriptions"],
        message: i18next.t(FIELD_MIN_TEXTS_VALID, { value: minDescriptions }),
      });
    } else if (adGroup.descriptions.length > maxDescriptions) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "descriptions"],
        message: i18next.t(FIELD_MAX_TEXTS_VALID, { value: maxDescriptions }),
      });
    }
    adGroup.descriptions?.forEach((item, idx) => {
      if (!item.value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "descriptions", idx, "value"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
      if (
        item.value &&
        item.value.length > ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE]
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "descriptions", idx, "value"],
          message: i18next.t(FIELD_MAX_LENGTH, {
            value: ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE],
          }),
        });
      }
      if (item.value && !validateNoEmojis(item.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "descriptions", idx, "value"],
          message: i18next.t(FIELD_EMOJI_NOT_ALLOWED),
        });
      }
    });

    // Validation for repeated texts in descriptions
    if (adGroup.descriptions && adGroup.descriptions.length > 0) {
      const descriptionsNormalized = adGroup.descriptions
        .map((item) => item.value?.trim().toLowerCase())
        .filter(Boolean);
      const descriptionsMap = getIndicesMap(descriptionsNormalized);

      Object.entries(descriptionsMap).forEach(([, indexes]) => {
        if (indexes.length > 1) {
          indexes.forEach((i) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "descriptions", i, "value"],
              message: i18next.t(FIELD_TEXT_REPEATED),
            });
          });
        }
      });
    }
  }
};
